import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { ClauseEditor } from '#src/routes/wizard/step-uis/adjudication/clause-editor.js';
import { useSpecActions } from '#src/routes/wizard/step-uis/adjudication/use-spec-actions.js';
import { useTRPC } from '#src/trpc.js';
import { SpecAccessErrorBoundary } from '../spec-access-error-boundary.js';

export const Route = createFileRoute('/_authorization-gate/wizard/admin/data-specs/')({
	head: () => ({ meta: [{ title: 'Data Specs • Wizard Admin • Cadstrom' }] }),
	errorComponent: SpecAccessErrorBoundary,
	component: WizardAdminDataSpecsPage,
});

/**
 * The package data specification over every property: the clause viewer and editor, outside any
 * adjudication review. Reads the whole catalogue, retired clauses and superseded wordings included,
 * so the editor's own toggles reveal them without a further read.
 *
 * @returns The page element.
 */
function WizardAdminDataSpecsPage() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	const {
		data: { clauses },
	} = useSuspenseQuery(
		trpc.packageDataSpec.clause.listCurrent.queryOptions({ includeRetired: true, includePriorVersions: true }),
	);

	const refetchCatalogue = useCallback(
		() => queryClient.invalidateQueries({ queryKey: trpc.packageDataSpec.clause.listCurrent.queryKey() }),
		[queryClient, trpc],
	);
	const spec = useSpecActions(refetchCatalogue);

	return (
		<div className='flex min-h-0 grow basis-0 flex-col p-3'>
			<ClauseEditor
				className='grow'
				clauses={clauses}
				onAdd={spec.addClause}
				onNewVersion={spec.onNewClauseVersion}
				onSupersede={spec.onSupersedeClause}
				onRetire={spec.onRetireClause}
				onSetReplacement={spec.onRecordClauseReplacement}
			/>
		</div>
	);
}
